import React, { useState, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';
import { ExpandableListButton, ExpandableListContainer, ExpandableListItem, ExpandableListWrapper } from './expandable-list.style';
import { breakpoints } from '../base/theme';

interface Props {
    data: string[];
    isEllipsis?: boolean;
    expandText: React.ReactNode;
    collapseText: React.ReactNode;
    iconType?: string;
    'data-testid'?: string;
}

const ExpandableList: React.FC<Props> = ({
    data,
    isEllipsis = false,
    expandText,
    collapseText,
    iconType,
    'data-testid': dataTestId
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isMobile = useMediaQuery({ maxWidth: breakpoints.medium });

    const initialCount = isMobile ? 4 : 8;
    const displayData = isExpanded ? data : data.slice(0, initialCount);

    const shouldShowButton = useMemo(() => {
        const dataLength = data.length;
        
        if (dataLength <= 4) {
            return false;
        } else if (dataLength <= 8) {
            return isMobile;
        } else {
            return true;
        }
    }, [data.length, isMobile]);

    return (
        <ExpandableListWrapper data-testid={dataTestId}>
            <ExpandableListContainer role="list" isMobile={isMobile} data-testid={`${dataTestId}-container`}>
                {displayData.map((item) => (
                    <ExpandableListItem 
                        role="listitem" 
                        key={item} 
                        isEllipsis={isEllipsis}
                        itemsPerRow={isMobile ? 1 : 2}
                        data-testid={`${dataTestId}-item`}
                    >
                        {iconType && <span className={`icon icon--${iconType}`} aria-hidden="true" />}
                        <span className="text-content">{item}</span>
                    </ExpandableListItem>
                ))}
            </ExpandableListContainer>

            {shouldShowButton && (
                <ExpandableListButton
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    aria-expanded={isExpanded}>
                    {isExpanded ? collapseText : expandText}
                </ExpandableListButton>
            )}
        </ExpandableListWrapper>
    );
};

export default ExpandableList;
